import React, { useEffect } from 'react';
import Cookies from 'js-cookie';
import { CONSENT_POPUP_MODAL_KEY } from '@/lib/constants';
import { markConsentNoticeSeen } from '@/lib/gtm';

type MODAL_VIEWS =
  | 'REGISTER'
  | 'LOGIN_VIEW'
  | 'FORGOT_VIEW'
  | 'NEW_PASSWORD_VIEW'
  | 'OTP_LOGIN'
  | 'ADD_OR_UPDATE_ADDRESS'
  | 'ADD_OR_UPDATE_CHECKOUT_CONTACT'
  | 'ADD_OR_UPDATE_PROFILE_CONTACT'
  | 'ADD_OR_UPDATE_GUEST_ADDRESS'
  | 'LOCATION_BASED_SHOP'
  | 'DELETE_ADDRESS'
  | 'PRODUCT_DETAILS'
  | 'REFUND_REQUEST'
  | 'REVIEW_RATING'
  | 'ORDER_REVIEW'
  | 'QUESTION_FORM'
  | 'ABUSE_REPORT'
  | 'SHOP_INFO'
  | 'SELECT_PRODUCT_VARIATION'
  | 'REVIEW_IMAGE_POPOVER'
  | 'USE_NEW_PAYMENT'
  | 'PAYMENT_MODAL'
  | 'STRIPE_ELEMENT_MODAL'
  | 'DELETE_CARD_MODAL'
  | 'ADD_NEW_CARD'
  | 'ADD_NEW_CARD_DURING_PAYMENT'
  | 'USE_SAVED_CARD'
  | 'GATEWAY_MODAL'
  | 'NEWSLETTER_MODAL'
  | 'PROMO_POPUP_MODAL'
  | 'REVIEW_POPUP_MODAL'
  | 'COMPARE_INFO'
  | 'DIGITAL_RECIPE_INFO'
  | 'RECIPE_PREVIEW'
  | 'BUY_WITHOUT_RECIPE'
  | 'USER_RELATIONSHIP_SELECT'
  | 'CONSENT_POPUP_MODAL'
  | 'SHARE_MAGIC_LINK'
  | 'FRIEND_INDICATION_RULES'
  | 'COUPON_PICKER'
  | 'APP_UPDATE_REQUIRED';

interface State {
  view?: MODAL_VIEWS;
  data?: any;
  isOpen: boolean;
}
type Action =
  | { type: 'open'; view?: MODAL_VIEWS; payload?: any }
  | { type: 'close' };

const initialState: State = {
  view: undefined,
  isOpen: false,
  data: null,
};

// Add type declaration for the global variable
declare global {
  interface Window {
    __modalActions?: {
      closeModal: () => void;
      openModal: (view?: MODAL_VIEWS, payload?: any) => void;
    };
  }
}

function modalReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'open':
      // If APP_UPDATE_REQUIRED is open, don't allow other modals to open
      if (state.view === 'APP_UPDATE_REQUIRED' && action.view !== 'APP_UPDATE_REQUIRED') {
        return state; // Keep current state, don't open new modal
      }
      return {
        ...state,
        view: action.view,
        data: action.payload,
        isOpen: true,
      };
    case 'close':
      // Don't allow closing APP_UPDATE_REQUIRED modal
      if (state.view === 'APP_UPDATE_REQUIRED') {
        return state; // Keep current state, don't close
      }
      return {
        ...state,
        view: undefined,
        data: null,
        isOpen: false,
      };
    default:
      throw new Error('Unknown Modal Action!');
  }
}

const ModalStateContext = React.createContext<State>(initialState);
ModalStateContext.displayName = 'ModalStateContext';
const ModalActionContext = React.createContext<
  React.Dispatch<Action> | undefined
>(undefined);
ModalActionContext.displayName = 'ModalActionContext';

export const ModalProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = React.useReducer(modalReducer, initialState);

  // Expose modal actions to global scope for native app
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__modalActions = {
        closeModal: () => dispatch({ type: 'close' }),
        openModal: (view?: MODAL_VIEWS, payload?: any) =>
          dispatch({ type: 'open', view, payload }),
      };
    }

    return () => {
      if (typeof window !== 'undefined') {
        delete window.__modalActions;
      }
    };
  }, []);

  return (
    <ModalStateContext.Provider value={state}>
      <ModalActionContext.Provider value={dispatch}>
        {children}
      </ModalActionContext.Provider>
    </ModalStateContext.Provider>
  );
};

export function useModalState() {
  const context = React.useContext(ModalStateContext);
  if (context === undefined) {
    throw new Error(`useModalState must be used within a ModalProvider`);
  }
  return context;
}

export function useModalAction() {
  const dispatch = React.useContext(ModalActionContext);
  const state = React.useContext(ModalStateContext);
  if (dispatch === undefined) {
    throw new Error(`useModalAction must be used within a ModalProvider`);
  }
  return {
    openModal(view?: MODAL_VIEWS, payload?: unknown) {
      // If APP_UPDATE_REQUIRED is open, don't allow other modals to open
      if (state.view === 'APP_UPDATE_REQUIRED' && view !== 'APP_UPDATE_REQUIRED') {
        return; // Don't open new modal
      }
      
      // Opening another modal dismisses the LGPD notice. It does not change
      // Google consent; storage is already granted for every visitor.
      if (
        view && 
        view !== 'CONSENT_POPUP_MODAL' && 
        view !== 'APP_UPDATE_REQUIRED' &&
        typeof window !== 'undefined'
      ) {
        const seenConsentPopup = Cookies.get(CONSENT_POPUP_MODAL_KEY);
        if (!seenConsentPopup) {
          markConsentNoticeSeen();
        }
      }
      
      dispatch({ type: 'open', view, payload });
    },
    closeModal() {
      // Don't allow closing APP_UPDATE_REQUIRED modal
      if (state.view === 'APP_UPDATE_REQUIRED') {
        return; // Don't close
      }
      dispatch({ type: 'close' });
      sessionStorage.setItem('redirect_after_login', '');
    },
  };
}
